import React, { useCallback, useEffect, useState } from "react";
import { Container, Row, Col, Button, Spinner, Alert } from "react-bootstrap";
import { supabase } from "../../config/Supabase";
import { useAuth } from "../../context/AuthContext";
import EarningsOverview from "./EarningsOverview";
import PromoCodeCard from "./PromoCodeCard";
import ConversionsTable from "./ConversionsTable";
import PayoutHistory from "./PayoutHistory";
import AffiliateProfile from "./AffiliateProfile";

const AffiliateDashboard = ({ affiliate: injectedAffiliate }) => {
  // Prefer the affiliate row injected by RequireAffiliate; fall back to AuthContext.
  const { affiliate: ctxAffiliate, refresh: refreshAuth, signOut } = useAuth();
  const affiliate = injectedAffiliate || ctxAffiliate;

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!affiliate?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: sum, error: sumErr } = await supabase
        .from("affiliate_dashboard_summary")
        .select("*")
        .eq("affiliate_id", affiliate.id)
        .maybeSingle();
      if (sumErr) throw sumErr;
      setSummary(
        sum || {
          paid_cents: 0,
          pending_payout_cents: 0,
          trial_pending_cents: 0,
          lifetime_earned_cents: 0,
          this_month_cents: 0,
          trials_started: 0,
          paid_conversions: 0,
          voided_count: 0,
          not_commissionable_count: 0,
        }
      );
    } catch (err) {
      console.error("Failed to load dashboard summary:", err);
      setError(err.message || "Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  }, [affiliate?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleProfileUpdated = useCallback(async () => {
    await refreshAuth();
    await refresh();
  }, [refresh, refreshAuth]);

  if (loading && !summary) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <Spinner animation="border" variant="success" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: "100px", maxWidth: "700px" }}>
        <Alert variant="danger">{error}</Alert>
        <Button variant="outline-secondary" onClick={signOut}>
          Sign out
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid="lg" style={{ paddingTop: "90px", paddingBottom: "60px" }}>
      <Row className="align-items-center mb-4 g-3">
        <Col xs={12} sm>
          <h3 style={{ marginBottom: "4px" }}>
            Welcome back, {affiliate?.display_name || "partner"}
          </h3>
          <p style={{ color: "#888", margin: 0 }}>
            Here's how your code{" "}
            <b style={{ fontFamily: "monospace" }}>{affiliate?.promo_code}</b>{" "}
            is performing.
          </p>
        </Col>
        <Col xs={12} sm="auto">
          <Button variant="outline-secondary" onClick={signOut}>
            Sign out
          </Button>
        </Col>
      </Row>

      <Row className="g-4">
        <Col xs={12}>
          <PromoCodeCard affiliate={affiliate} />
        </Col>

        <Col xs={12}>
          <EarningsOverview summary={summary} />
        </Col>

        <Col xs={12}>
          <ConversionsTable affiliateId={affiliate?.id} />
        </Col>

        <Col xs={12} lg={6}>
          <PayoutHistory affiliateId={affiliate?.id} />
        </Col>

        <Col xs={12} lg={6}>
          <AffiliateProfile affiliate={affiliate} onUpdated={handleProfileUpdated} />
        </Col>
      </Row>
    </Container>
  );
};

export default AffiliateDashboard;
